import React, { useEffect, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { mountRootParcel } from 'single-spa';
import Parcel from 'single-spa-react/parcel';

import { createReactRenderer } from 'helpers/react-renderer';
import { InvoicingConfig } from 'root/apps/QuoteMachineIntegration/common';
import { useAutoARSettings } from 'root/apps/QuoteMachineIntegration/hooks/useAutoARSettings';
import { useSale } from 'root/apps/QuoteMachineIntegration/hooks/useSale';
import AutomatedAccountReceivablesService from 'root/apps/QuoteMachineIntegration/services/AutomatedAccountReceivablesService';
import invoiceService from 'root/apps/QuoteMachineIntegration/services/InvoiceService';
import invoiceServiceV1 from 'root/apps/QuoteMachineIntegration/services/v1/InvoiceService';
import { InvoicingModalProps } from 'root/apps/QuoteMachineIntegration/state/InvoicingModalProps';
import { InvoicingOption } from 'root/apps/QuoteMachineIntegration/state/InvoicingOption';
import { InvoiceObjectData } from 'root/apps/QuoteMachineIntegration/state/v1/InvoicePreviewDto';
import { SystemJS } from 'root/apps/QuoteMachineIntegration/SystemJS';
import { logInvoiceCreationMetric } from 'root/apps/QuoteMachineIntegration/utils/datadogLogging';
import { getSaleLineCount } from 'root/apps/QuoteMachineIntegration/utils/saleHelpers';
import { getSessionData } from 'root/apps/QuoteMachineIntegration/utils/SessionData';
import { datadogRum } from 'root/datadog/datadogRum';
import Modules from 'shared_modules/Modules';

import {
  LsInvoicingCloseInvoicePreviewTime,
  LsInvoicingInvoiceCreationTime,
} from '../datadogEventConstants';
import PaymentTermsService from '../services/v1/PaymentTermsService';
import { InvoicingFormData } from '../state/InvoicingFormData';

export const errorMessages = {
  CANCEL_ERROR: 'There was an error deleting the invoice for this sale.',
  UPDATE_ERROR: 'There was an error updating the invoice for this sale.',
  CREATE_ERROR: 'There was an error creating the invoice for this sale.',
  PARCEL_ERROR: 'There was an error loading the invoicing modal.',
  FETCH_LOCATION_ERROR: 'There was an error fetching the shop information.',
  AUTO_AR_ERROR:
    'There was an error loading the automated account receivables settings for this customer.',
  INVOICE_INTENT_CREATE_ERROR: 'There was an error creating the invoice intent for this sale.',
};

function InvoicingModal({
  transactionId,
  payments,
  invoicePayments,
  depositPayments,
  giftCardPayments,
  depositToAdd,
  onCancel,
  onSubmit,
  onError,
  openInvoiceModalStartTime,
  customerCreditAccountId = 0,
  invoiceData,
  documentPreviewURL,
  defaultToLegacy = false,
}: InvoicingModalProps) {
  const { sale, loading: loadingSale } = useSale(transactionId);
  const isV1 = Modules.hasModule('invoicing.api.v1') && !defaultToLegacy;

  const {
    loading: loadingAutoArSettings,
    error: autoArError,
    accountStatus: autoArAccountStatus,
    customerStatus: autoArCustomerStatus,
  } = useAutoARSettings(customerCreditAccountId.toString());

  const {
    isLoading: creatingInvoice,
    isError: isErrorCreatingInvoice,
    data: legacyInvoiceData,
  } = useQuery({
    queryKey: ['createInvoice', sale],
    queryFn: async () => {
      const result = await invoiceService.createNewInvoice(
        sale ?? ({} as Sale),
        payments,
        invoicePayments,
        depositPayments,
        giftCardPayments,
        depositToAdd
      );
      logInvoiceMetricForSale(LsInvoicingInvoiceCreationTime, openInvoiceModalStartTime, 'legacy');

      if (!result.data?.attributes?.proposalURL) {
        onError(errorMessages.CREATE_ERROR);
      }

      return result;
    },
    enabled: !loadingSale && !isV1,
    retry: false,
    refetchOnWindowFocus: false,
    cacheTime: 0,
  });

  const loading = useMemo(() => {
    // If using the old API, wait for invoice creation and Auto AR settings
    if (!isV1) {
      return creatingInvoice || loadingAutoArSettings;
    }
    // If using the new API, only wait for Auto AR settings
    // (invoice data should already be provided via props)
    return loadingAutoArSettings;
  }, [creatingInvoice, isV1, loadingAutoArSettings]);

  useEffect(() => {
    window.showNotifier('working');
    window.hideNotifier('warning');
  }, []);

  const logInvoiceMetricForSale = (
    actionName: string,
    startTime: number,
    apiVersion?: 'legacy' | 'v1'
  ) => {
    logInvoiceCreationMetric({
      actionName,
      saleID: transactionId,
      startTime,
      numberOfPayments: Object.keys(payments).length,
      numberOfDepositPayments: depositPayments.length,
      numberOfItems: getSaleLineCount(sale),
      ...(apiVersion && { apiVersion }),
    });
  };

  function handleCancel() {
    if (!sale) {
      return;
    }

    const finalizeInvoiceModalStartTime = new Date(Date.now()).getTime();

    // V1 API doesn't create an invoice during preview, so nothing to delete
    if (isV1) {
      logInvoiceMetricForSale(LsInvoicingCloseInvoicePreviewTime, finalizeInvoiceModalStartTime);
      onCancel();
      return;
    }

    // Legacy flow created an invoice in Quote Machine DB - delete it

    window.showNotifier('working');
    invoiceService
      .deleteInvoice(sale.saleID)
      .then(() => {
        logInvoiceMetricForSale(LsInvoicingCloseInvoicePreviewTime, finalizeInvoiceModalStartTime);
        onCancel();
      })
      .catch(handleCancelError)
      .finally(() => window.hideNotifier('working'));
  }

  function handleCancelError() {
    onError(errorMessages.CANCEL_ERROR);
  }

  function isSaleInvalid(sale: Sale) {
    const invalidStates: (keyof Sale)[] = ['archived', 'completed', 'voided'];
    return invalidStates.some((state) => sale[state] === 'true');
  }

  function handleSubmit(formData: InvoicingFormData) {
    if (!sale || isSaleInvalid(sale)) {
      return;
    }
    const finalizeInvoiceModalStartTime = new Date(Date.now()).getTime();

    const payload: InvoiceUpdate = {
      sendEmail: formData.option === InvoicingOption.SEND,
      dueDate: formData.dueDate,
      email: formData.email,
      note: formData.note,
    };

    if (!autoArError) {
      // No await because we want this call to be done asynchronously in the
      // background and not block the process if it fails.
      AutomatedAccountReceivablesService.processAutoArOptions(
        formData.automatedAccountReceivablesOptions,
        customerCreditAccountId.toString()
      );
    }

    // Create invoice for V1 API, otherwise update invoice in legacy flow
    if (isV1 && sale.Customer?.creditAccountID) {
      const invoiceObjectData: InvoiceObjectData | undefined = invoiceData?.objectData;

      // Update the invoice terms with form data
      if (invoiceObjectData?.invoice) {
        invoiceObjectData.invoice.terms = {
          sendByEmail: formData.option === InvoicingOption.SEND,
          customerEmail: String(formData.email),
          dueDate: formData.dueDate.toISOString().split('T')[0],
          notes: String(formData.note),
        };
      }

      window.showNotifier('working');
      invoiceServiceV1
        .upsertInvoiceIntent(
          String(sale.saleID),
          String(sale.Customer.creditAccountID),
          invoiceObjectData
        )
        .then(() => onSubmit(formData.option, finalizeInvoiceModalStartTime))
        .catch(handleSubmitError)
        .finally(() => window.hideNotifier('working'));
    } else {
      window.showNotifier('working');
      invoiceService
        .updateInvoice(sale.saleID, payload)
        .then(() => onSubmit(formData.option, finalizeInvoiceModalStartTime))
        .catch(handleSubmitError)
        .finally(() => window.hideNotifier('working'));
    }
  }

  function handleSubmitError() {
    if (isV1) {
      onError(errorMessages.INVOICE_INTENT_CREATE_ERROR);
    } else {
      onError(errorMessages.UPDATE_ERROR);
    }
  }

  if (isErrorCreatingInvoice) {
    window.hideNotifier('working');
    onError(errorMessages.CREATE_ERROR);
    return <></>;
  }

  if (sale && !loading) {
    window.hideNotifier('working');
    return (
      <Parcel
        key="invoicing-modal"
        mountParcel={mountRootParcel}
        baseLocation="/register.php"
        skipCSSUnmount={true}
        sessionData={getSessionData(sale)}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        proposalURL={legacyInvoiceData?.data?.attributes?.proposalURL}
        invoiceData={invoiceData}
        documentPreviewURL={documentPreviewURL}
        defaultToLegacy={defaultToLegacy}
        showAutomatedAccountReceivablesWidget={!loadingAutoArSettings && !autoArError}
        automatedAccountReceivablesOptions={{
          customerEnabled: autoArCustomerStatus,
          enabledByDefault: autoArAccountStatus,
        }}
        getDefaultPaymentTermSettings={PaymentTermsService.getDefaultPaymentTermSettings}
        config={async () =>
          (await SystemJS.import<InvoicingConfig>('@lightspeed/invoicing')).default
            .InvoicingModalConfig
        }
        handleError={(err: Error) => datadogRum.addError(err)}
      />
    );
  }

  return <></>;
}

export { InvoicingModal };

export const renderInvoicingModal = (props: InvoicingModalProps) => {
  const RenderedComponent = createReactRenderer({
    component: InvoicingModal,
    selector: '[data-react-root="ReactModal"]',
  });

  RenderedComponent.mount({
    props: { ...props },
  });
};
